import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { OpsHeading, OpsState, OpsTable, errorText } from './OperationsUI';
import { toast } from '@/components/ui/sonner';
import { RefreshCw, Search, Trash2 } from 'lucide-react';
import api from '@/lib/api';

const ROLES = ['OWNER', 'ADMIN', 'CASHIER', 'PICKER', 'VIEWER', 'DRIVER'];

export const PlatformUsers = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);
  const [orgIndex, setOrgIndex] = useState([]);
  const [newMem, setNewMem] = useState({ org_id: '', store_id: '__ORG__', role: 'VIEWER' });
  const [confirmRemove, setConfirmRemove] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try { setRows((await api.get('/platform/users')).data); }
    catch (e) { setError(errorText(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/platform/org-index').then(({ data }) => setOrgIndex(data)).catch(() => {}); }, []);

  const patchUser = (u) => setRows((prev) => prev.map((r) => (r.id === u.id ? { ...r, ...u } : r)));

  const addMembership = async () => {
    if (!active || !newMem.org_id) { toast.error('Selecciona una organización'); return; }
    setBusy(true);
    try {
      const store_id = newMem.store_id === '__ORG__' ? null : newMem.store_id;
      await api.post('/platform/memberships', { user_id: active.id, org_id: newMem.org_id, store_id, role: newMem.role });
      const fresh = (await api.get('/platform/users')).data;
      setRows(fresh);
      const updated = fresh.find((r) => r.id === active.id);
      if (updated) setActive(updated);
      setNewMem({ org_id: '', store_id: '__ORG__', role: 'VIEWER' });
      toast.success('Membresía añadida');
    } catch (e) { toast.error(errorText(e)); }
    finally { setBusy(false); }
  };
  const selectedOrg = orgIndex.find((o) => o.org_id === newMem.org_id);

  const toggleAccount = async (u, value) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/platform/users/${u.id}/active`, { active: value });
      patchUser({ id: u.id, active: data.active });
      if (active && active.id === u.id) setActive({ ...active, active: data.active });
      toast.success(value ? 'Cuenta activada' : 'Cuenta desactivada');
    } catch (e) { toast.error(errorText(e)); }
    finally { setBusy(false); }
  };

  const updateMembership = async (m, patch) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/platform/memberships/${m.id}`, patch);
      const next = { ...active, memberships: active.memberships.map((x) => (x.id === m.id ? { ...x, ...data } : x)) };
      setActive(next); patchUser(next);
      toast.success('Membresía actualizada');
    } catch (e) { toast.error(errorText(e)); }
    finally { setBusy(false); }
  };

  const removeMembership = async (m) => {
    setBusy(true);
    try {
      await api.delete(`/platform/memberships/${m.id}`);
      const next = { ...active, memberships: active.memberships.filter((x) => x.id !== m.id) };
      setActive(next); patchUser(next);
      toast.success('Membresía eliminada');
    } catch (e) { toast.error(errorText(e)); }
    finally { setBusy(false); setConfirmRemove(null); }
  };

  const filtered = rows.filter((r) => !q || (`${r.email} ${r.full_name || ''}`).toLowerCase().includes(q.toLowerCase()));

  return (
    <>
      <OpsHeading title="Consola de usuarios" description="Todas las cuentas del mercado: clientes, equipos de tienda y administradores. Activa/desactiva cuentas y ajusta el rol de cada membresía.">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por correo o nombre" data-testid="users-search" className="pl-9 w-64" />
        </div>
        <Button variant="outline" data-testid="users-reload" onClick={load}><RefreshCw /> Actualizar</Button>
      </OpsHeading>
      <OpsState loading={loading} error={error} reload={load}>
        <OpsTable
          id="platform-users-table"
          rows={filtered}
          columns={[
            { key: 'email', label: 'Cuenta', render: (r) => <span>{r.full_name || '—'}<br /><span className="text-xs text-muted-foreground">{r.email}</span></span> },
            { key: 'type', label: 'Tipo', render: (r) => r.is_super_admin ? <Badge data-testid={`user-role-${r.id}`}>SuperAdmin</Badge> : (r.memberships?.length ? <Badge variant="secondary">Vendedor</Badge> : <Badge variant="outline">Cliente</Badge>) },
            { key: 'memberships', label: 'Membresías', render: (r) => r.memberships?.length || 0 },
            { key: 'active', label: 'Estado', render: (r) => <Badge variant={r.active === false ? 'outline' : 'secondary'} data-testid={`user-active-${r.id}`}>{r.active === false ? 'Inactiva' : 'Activa'}</Badge> },
          ]}
          actions={(r) => <Button variant="outline" data-testid={`user-open-${r.id}`} onClick={() => setActive(r)}>Gestionar</Button>}
        />
      </OpsState>
      <Dialog open={!!active} onOpenChange={(v) => { if (!v && !busy) setActive(null); }}>
        <DialogContent className="seller-dialog" data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle>{active?.full_name || active?.email}</DialogTitle>
            <DialogDescription>{active?.email}{active?.is_super_admin ? ' · SuperAdmin (dominio verificado)' : ''}</DialogDescription>
          </DialogHeader>
          {active && (
            <div className="space-y-5">
              <div className="flex items-center justify-between border border-border rounded-lg p-3">
                <div><Label className="text-sm">Cuenta activa</Label><p className="text-xs text-muted-foreground">Al desactivar, el usuario no podrá iniciar sesión ni operar.</p></div>
                <Switch checked={active.active !== false} disabled={busy || active.is_super_admin} data-testid="user-active-switch" onCheckedChange={(v) => toggleAccount(active, v)} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Membresías de tienda / organización</Label>
                {active.memberships?.length ? (
                  <div className="space-y-2 mt-2" data-testid="user-memberships">
                    {active.memberships.map((m) => (
                      <div key={m.id} className="border border-border rounded-lg p-3 space-y-2" data-testid={`membership-${m.id}`}>
                        <p className="text-sm font-medium">{m.org_name || m.org_id}</p>
                        <p className="text-xs text-muted-foreground">{m.store_name}</p>
                        <div className="flex items-center gap-3 flex-wrap">
                          <Select value={m.role} onValueChange={(v) => updateMembership(m, { role: v })}>
                            <SelectTrigger className="w-40" data-testid={`membership-role-${m.id}`}><SelectValue /></SelectTrigger>
                            <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                          </Select>
                          <div className="flex items-center gap-2"><Switch checked={m.active !== false} disabled={busy} data-testid={`membership-active-${m.id}`} onCheckedChange={(v) => updateMembership(m, { active: v })} /><span className="text-xs text-muted-foreground">{m.active !== false ? 'Activa' : 'Inactiva'}</span></div>
                          <Button variant="ghost" size="sm" className="ml-auto text-destructive hover:text-destructive" disabled={busy} data-testid={`membership-remove-${m.id}`} onClick={() => setConfirmRemove(m)}><Trash2 className="h-4 w-4" /> Quitar</Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-sm text-muted-foreground mt-2">Cuenta de cliente sin membresías de vendedor.</p>}
              </div>
              <div className="border-t border-border pt-4" data-testid="add-membership">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Añadir membresía</Label>
                <div className="grid gap-2 mt-2 sm:grid-cols-2">
                  <Select value={newMem.org_id} onValueChange={(v) => setNewMem({ ...newMem, org_id: v, store_id: '__ORG__' })}>
                    <SelectTrigger data-testid="add-membership-org"><SelectValue placeholder="Organización" /></SelectTrigger>
                    <SelectContent>{orgIndex.map((o) => <SelectItem key={o.org_id} value={o.org_id}>{o.org_name || o.org_id}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={newMem.store_id} onValueChange={(v) => setNewMem({ ...newMem, store_id: v })} disabled={!newMem.org_id}>
                    <SelectTrigger data-testid="add-membership-store"><SelectValue placeholder="Tienda" /></SelectTrigger>
                    <SelectContent><SelectItem value="__ORG__">Matriz de organización</SelectItem>{(selectedOrg?.stores || []).map((s) => <SelectItem key={s.id} value={s.id}>{s.name || s.id}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={newMem.role} onValueChange={(v) => setNewMem({ ...newMem, role: v })}>
                    <SelectTrigger data-testid="add-membership-role"><SelectValue /></SelectTrigger>
                    <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button onClick={addMembership} disabled={busy || !newMem.org_id} data-testid="add-membership-save" className="seller-primary">Añadir</Button>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">El rol de SuperAdmin se asigna por dominio de correo verificado en Google y no se gestiona aquí.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <AlertDialog open={!!confirmRemove} onOpenChange={(v) => { if (!v && !busy) setConfirmRemove(null); }}>
        <AlertDialogContent data-testid="membership-remove-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Quitar membresía</AlertDialogTitle>
            <AlertDialogDescription>
              Se retirará el acceso de <strong>{active?.full_name || active?.email}</strong> a <strong>{confirmRemove?.org_name || confirmRemove?.org_id}</strong> · {confirmRemove?.store_name}. Se conserva el historial y puedes volver a crearla más tarde.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy} data-testid="membership-remove-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction disabled={busy} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="membership-remove-confirm" onClick={(e) => { e.preventDefault(); removeMembership(confirmRemove); }}>{busy ? 'Quitando…' : 'Quitar membresía'}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default PlatformUsers;
